import React, { useCallback } from "react";
import { updateOrganizationIntegrationSettings } from "../../../../api-client/organization/OrganizationIntegrationSettingsAPIClient";
import { BlueprintSpeed, OrganizationIntegrationSettings } from "../../../../models/Entities";
import { displayNameForBlueprintSpeed } from "../../../../models/Helpers";
import { ProductRestrictions } from "../../settings/billing/BillingModels";
import useProductRestrictions from "../../../shared/hooks/useProductRestrictions";
import MultiSwitch from "../../../shared/MultiSwitch";
import { BLUEPRINT_SPEED_COLORS } from "./IntegrationSettingsRowOverview";
import { showErrorToast } from "../../../shared/Toasts";
import { Tooltip, Text } from "@merge-api/merge-javascript-shared";

interface Props {
  /**
   * All settings for this integration
   */
  settings: OrganizationIntegrationSettings;

  /**
   * Something happens when we toggle the integration - this dictates it!
   */
  onIntegrationToggle: (data: OrganizationIntegrationSettings) => void;
}

// All speeds we support
const SPEEDS = [BlueprintSpeed.SLOW, BlueprintSpeed.INTERMEDIATE, BlueprintSpeed.FAST];

/**
 * Creates an option for a given speed from speed + restrictions
 */
const optionForSpeed = (
  speed: BlueprintSpeed,
  isCustomSpeedSet: boolean,
  restrictions?: ProductRestrictions,
) => {
  const isDisabled =
    restrictions === undefined ||
    !restrictions.available_sync_speeds.includes(speed) ||
    isCustomSpeedSet;
  const toolTipMessage = isCustomSpeedSet
    ? "Your organization has a custom agreement in place with Merge. To modify sync frequency, please contact us."
    : "Upgrade your plan to unlock higher sync frequencies";
  return {
    text: displayNameForBlueprintSpeed(speed),
    id: speed,
    selectedColor: BLUEPRINT_SPEED_COLORS[speed],
    disable: isDisabled,
    disableTooltip: toolTipMessage,
  };
};

/**
 * Shows a section for the sync frequency & any upsells that exist
 */
const IntegrationSyncFrequencySection = ({ settings, onIntegrationToggle }: Props) => {
  const { productRestrictions } = useProductRestrictions();
  const hasCustomBlueprintSpeed = settings.blueprint_speed === BlueprintSpeed.CUSTOM;
  const speedChoices = hasCustomBlueprintSpeed ? SPEEDS.concat([BlueprintSpeed.CUSTOM]) : SPEEDS;

  const setBlueprintSpeed = useCallback(
    (newSpeed: BlueprintSpeed) => {
      updateOrganizationIntegrationSettings({
        settingsId: settings.id,
        body: {
          blueprint_speed: newSpeed,
        },
        onUpdate: (data: OrganizationIntegrationSettings) => {
          onIntegrationToggle(data);
        },
        onError: () => {
          showErrorToast(`Failed to update sync frequency for ${settings.integration.name}`);
        },
      });
    },
    [updateOrganizationIntegrationSettings, onIntegrationToggle, settings.id],
  );

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-10">
      <Tooltip
        title="A higher frequency instructs Merge to make data sync requests to third party APIs
            more often. The cadence and number of requests varies by integration depending on
            each API's intricacies."
      >
        <Text variant="h6" className="underline decoration-dotted cursor-help">
          Sync frequency
        </Text>
      </Tooltip>
      <MultiSwitch
        className={hasCustomBlueprintSpeed ? "opacity-30" : ""}
        options={speedChoices.map((speed) =>
          optionForSpeed(speed, hasCustomBlueprintSpeed, productRestrictions),
        )}
        isDisabled={hasCustomBlueprintSpeed}
        selectedID={settings.blueprint_speed}
        onSelectOption={(option) =>
          setBlueprintSpeed(BlueprintSpeed[option.id as keyof typeof BlueprintSpeed])
        }
      />
    </div>
  );
};

export default IntegrationSyncFrequencySection;
